import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AppEvent } from '../store/eventsSlice'
import type { ParticipantRole } from '../store/registrationsSlice'
import { formatLocalDateTime } from '../utils/datetime'
import {
  apiRoleToUiRole,
  uiRoleToApiRole,
  formatFullName,
  hasGrantedMediaConsent,
} from '../utils/participantMappers'
import { hasEventEnded } from '../utils/event'
import { downloadCertificateOfAttendance, normaliseLanguage } from '../utils/downloadCoa'
import { RoleSelect } from './RoleBadge'
import { Button } from './Button'
import { ParticipantTicketButton } from './ParticipantTicketButton'
import { DetailsToggleButton } from './DetailsToggleButton'
import { ConfirmationModal } from './ConfirmationModal'
import { PresentationActionModal } from './PresentationActionModal'
import { ParticipantConversationModal } from './ParticipantConversationModal'
import { GenerateReportButton } from './GenerateReportButton'
import { TablePagination } from './TablePagination'
import { SortableColumnHeader } from './Table'
import { intersectEligibleIds } from '../utils/bulkActions'
import { usePagedParticipants } from '../hooks/usePagedParticipants'
import {
  useBatchUpdateParticipantsMutation,
  useAssignRoleMutation,
  useSendCertificatesOfAttendanceMutation,
  useSendEventMediaNotificationsMutation,
  useListMediaQuery,
  useClearAttendanceAdminMutation,
  type ParticipantSummary,
  type ListMediaResponse,
  type SendCertificatesResponse,
  type SendEventMediaNotificationResponse,
} from '@proaxiv/server-api'

// ── Icons ─────────────────────────────────────────────────────────────────────

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const CertificateIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="6" />
    <path d="M9 13.5 7 22l5-3 5 3-2-8.5" />
  </svg>
)

const SendMediaIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7z" />
  </svg>
)

const ClockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
)

const UserXIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="17" y1="8" x2="22" y2="13" />
    <line x1="22" y1="8" x2="17" y2="13" />
  </svg>
)

function MarkNotAttendedButton({
  onClick,
  fullWidth = false,
  className = '',
}: {
  onClick: () => void
  fullWidth?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <Button
      variant="secondary"
      size="sm"
      fullWidth={fullWidth}
      className={className}
      leftIcon={<UserXIcon />}
      onClick={onClick}
    >
      {t('att_mark_not_attended')}
    </Button>
  )
}

/** Per-row equivalent of the bulk "Send Certificate of Attendance" toolbar action — same
 *  eligibility (event ended) and same confirmation flow, just scoped to a single participant so
 *  it doesn't require selecting the row's checkbox first. */
function SendCertificateButton({
  onClick,
  disabled,
  fullWidth = false,
  className = '',
}: {
  onClick: () => void
  disabled?: boolean
  fullWidth?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <Button
      variant="secondary"
      size="sm"
      fullWidth={fullWidth}
      className={className}
      leftIcon={<CertificateIcon />}
      onClick={onClick}
      disabled={disabled}
    >
      {t('coa_action_send')}
    </Button>
  )
}

/** Per-row equivalent of the bulk "Send Event Media" toolbar action — same eligibility (event
 *  ended and at least one file uploaded) and same confirmation flow, just scoped to a single
 *  participant so it doesn't require selecting the row's checkbox first. */
function SendMediaButton({
  onClick,
  disabled,
  fullWidth = false,
  className = '',
}: {
  onClick: () => void
  disabled?: boolean
  fullWidth?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <Button
      variant="secondary"
      size="sm"
      fullWidth={fullWidth}
      className={className}
      leftIcon={<SendMediaIcon />}
      onClick={onClick}
      disabled={disabled}
    >
      {t('media_action_send')}
    </Button>
  )
}

// ── Delete / conversation buttons ────────────────────────────────────────────

function DeleteButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      variant="unstyled"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="text-pax-red hover:bg-pax-red/10 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-150 active:scale-90"
    >
      <TrashIcon />
    </Button>
  )
}

function ConversationButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      variant="unstyled"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="text-pax-gray hover:bg-pax-purple/15 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-150 active:scale-90"
    >
      <ClockIcon />
    </Button>
  )
}

// ── Expanded detail row (desktop) ────────────────────────────────────────────

function ExpandedRow({
  row,
  colSpan,
  eventId,
  eventHasEnded,
  coaSending,
  mediaEligible,
  mediaSending,
  onMarkNotAttended,
  onSendCoa,
  onSendMedia,
}: {
  row: ParticipantSummary
  colSpan: number
  eventId: string
  eventHasEnded: boolean
  coaSending: boolean
  mediaEligible: boolean
  mediaSending: boolean
  onMarkNotAttended: (participantId: string) => void
  onSendCoa: (participantId: string) => void
  onSendMedia: (participantId: string) => void
}) {
  const { t } = useTranslation()
  return (
    <tr className="bg-pax-light-purple/20">
      <td colSpan={colSpan} className="px-6 py-4">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_last_name')}
            </span>
            <p className="text-pax-black">{row.lastName || '—'}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_first_name')}
            </span>
            <p className="text-pax-black">{row.firstName}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_email')}
            </span>
            <p className="text-pax-black">{row.email}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_company')}
            </span>
            <p className="text-pax-black">{row.companyName ?? '—'}</p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_media')}
            </span>
            <p className="text-pax-black">
              {hasGrantedMediaConsent(row) ? t('reg_media_yes') : t('reg_media_no')}
            </p>
          </div>
          <div>
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('reg_field_registered_via')}
            </span>
            <p className="text-pax-black">
              {row.registeredByOrganizer ? t('reg_via_organizer') : t('reg_via_self')}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <span className="text-pax-gray text-xs font-semibold uppercase">
            {t('acc_additional_actions')}
          </span>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <ParticipantTicketButton eventId={eventId} participantId={row.id} />
            {eventHasEnded && (
              <SendCertificateButton onClick={() => onSendCoa(row.id!)} disabled={coaSending} />
            )}
            {mediaEligible && (
              <SendMediaButton onClick={() => onSendMedia(row.id!)} disabled={mediaSending} />
            )}
            <MarkNotAttendedButton onClick={() => onMarkNotAttended(row.id!)} />
          </div>
        </div>
      </td>
    </tr>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  event: AppEvent
}

/**
 * Participants who have checked in — moved here automatically once the QR-scan kiosk (or the
 * manual "Mark as attended" action) records their attendance, transitioning their status to
 * {@code ATTENDED}.
 *
 * Also the only place a Certificate of Attendance (CoA) can be generated and sent
 * (EP-13 / EP-11) — participants outside this table are never eligible. Sending calls
 * {@link useSendCertificatesOfAttendanceMutation}, which generates (first send) or re-sends
 * (subsequent sends) the PDF on the backend; the resulting {@code certificateLastSentAt} is
 * read straight off the participant summary once the participant list re-fetches, unlocking
 * the download button on the participant's ticket page. Per the post-event BPMN
 * (docs/bpmn/012_post_event.bpmn), this action only becomes available once the event has
 * ended — it's hidden while the event is still ongoing, even for attended participants.
 *
 * Also hosts the "Send Event Media" batch action (EP-15) — enabled once the organizer has
 * uploaded at least one file via the Overview tab's media panel, and (like CoA) only once the
 * event has ended. Sending calls {@link useSendEventMediaNotificationsMutation}; the resulting
 * {@code mediaLastSentAt} is read straight off the participant summary once the list re-fetches.
 * That timestamp is also a visibility cutoff on the backend: the ticket page's media gallery
 * only shows files uploaded at or before the participant's last send, so uploading more media
 * after sending does not change what an already-notified participant sees until the organizer
 * sends again.
 */
export function AttendedParticipantsTab({ event }: Props) {
  const { t, i18n } = useTranslation()

  // Most recent check-in first by default — the organiser scanning people in at the door cares
  // about who just walked in, not who arrived hours ago (see #174).
  const {
    participants: rows,
    total,
    offset,
    take,
    sort,
    order,
    setOffset,
    setTake,
    toggleSort,
  } = usePagedParticipants({
    eventId: event.id,
    status: 'ATTENDED',
    defaultSort: 'attendedAt',
    defaultOrder: 'desc',
  })

  const [batchUpdate] = useBatchUpdateParticipantsMutation()
  const [assignRole] = useAssignRoleMutation()
  const [sendCertificates, { isLoading: coaSending }] = useSendCertificatesOfAttendanceMutation()
  const [sendMedia, { isLoading: mediaSending }] = useSendEventMediaNotificationsMutation()
  const [clearAttendanceAdmin] = useClearAttendanceAdminMutation()
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null)
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    id: string
    name: string
    role: ParticipantRole
  } | null>(null)
  const [confirmCoaIds, setConfirmCoaIds] = useState<string[] | null>(null)
  const [confirmMediaIds, setConfirmMediaIds] = useState<string[] | null>(null)
  const [confirmUnmarkIds, setConfirmUnmarkIds] = useState<string[] | null>(null)
  const [coaSendError, setCoaSendError] = useState<string | null>(null)
  const [mediaSendError, setMediaSendError] = useState<string | null>(null)
  const [unmarkError, setUnmarkError] = useState<string | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [unmarkLoading, setUnmarkLoading] = useState(false)

  // Gates the "Send Event Media" batch action — only shown once the organizer has uploaded at
  // least one file from the Overview tab's media panel (EP-14).
  const { data: mediaData } = useListMediaQuery({ eventId: event.id! }, { skip: !event.id })
  const mediaCount = (mediaData as ListMediaResponse | undefined)?.media?.length ?? 0

  // Per the post-event BPMN (docs/bpmn/012_post_event.bpmn), CoA generation/sending and media
  // notification only start once the event has ended — organizers can't trigger either action
  // while the event is still ongoing.
  const eventHasEnded = hasEventEnded(event)

  function handleSelectRow(key: string, checked: boolean) {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  function handleSelectAll(checked: boolean) {
    setSelectedKeys(checked ? new Set(rows.map((r) => r.id!)) : new Set())
  }

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [conversationFor, setConversationFor] = useState<{ id: string; name: string } | null>(null)

  function openConversation(row: ParticipantSummary) {
    setConversationFor({
      id: row.id!,
      name: formatFullName(row.firstName, row.lastName) || (row.email ?? ''),
    })
  }

  function toggleExpand(id: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** Reverts an accidental check-in — the participant moves back to the Accepted tab once the
   *  participant list is refetched (see `clearAttendanceAdmin`'s tag invalidation). */
  async function handleMarkNotAttended(participantId: string) {
    await clearAttendanceAdmin({ eventId: event.id, participantId })
  }

  // ── Role change (issue #226) ────────────────────────────────────────────────
  //
  // How this works: the engine's `assignRole` endpoint (EventParticipantService.assignRole)
  // already permitted the role update for participants in either ACCEPTED *or* ATTENDED status
  // — the gap closed here was purely on the frontend, which only wired the interactive
  // `RoleSelect` + confirmation flow into AcceptedParticipantsTab, leaving this tab with a
  // read-only `RoleBadge`. So the fix is: swap `RoleBadge` for the same `RoleSelect` control,
  // reuse the exact same `useAssignRoleMutation` hook, and reuse the exact same
  // "pick → confirm → apply" flow (down to the same `acc_role_change_confirm_title` i18n copy)
  // that AcceptedParticipantsTab already established for TC-PART-020 — no new backend endpoint,
  // no new confirmation-modal variant, just the existing building blocks mounted a second time.
  //
  // Why it's safe for a participant who has already checked in: `assignRole`'s mutation tag
  // invalidation (`EventParticipants` + `EventParticipantActivity`) refetches every screen that
  // reads participant/role data — the ticket page's role badge, the presentation-eligibility
  // gates (Speaker/Host only), the Certificate of Attendance (role is baked into its content hash
  // and its "role line", so a change here correctly regenerates the certificate on the next
  // Generate-and-send instead of silently keeping a stale one — see engine test
  // `sendCertificatesOfAttendance_regenerates_after_role_change`), and the attendance/role
  // exports — all pick up the new role automatically rather than needing a manual refresh or a
  // one-off patch to each of those call sites.
  function requestRoleChange(id: string, name: string, role: ParticipantRole) {
    setPendingRoleChange({ id, name, role })
  }

  async function handleConfirmRoleChange(result: { notify: boolean; message: string }) {
    if (!pendingRoleChange) return
    const { id, role } = pendingRoleChange
    await assignRole({
      participantId: id,
      assignRoleBody: {
        role: uiRoleToApiRole(role) as 'AUDIENCE' | 'SPEAKER' | 'STAFF' | 'HOST',
        sendEmailNotification: result.notify,
        message: result.message || undefined,
      },
    })
    setPendingRoleChange(null)
  }

  async function handleConfirmDelete() {
    if (!confirmDelete) return
    await batchUpdate({
      eventId: event.id,
      batchBody: {
        participantIds: confirmDelete,
        action: 'DELETE',
        sendEmailNotification: false,
      },
    })
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      confirmDelete.forEach((id) => next.delete(id))
      return next
    })
    setConfirmDelete(null)
  }

  /** Clears attendance for selected participants (post-event organiser backup). */
  async function handleConfirmUnmark() {
    if (!confirmUnmarkIds) return
    const ids = confirmUnmarkIds
    setConfirmUnmarkIds(null)
    setUnmarkError(null)
    setUnmarkLoading(true)
    try {
      const response = (await batchUpdate({
        eventId: event.id,
        batchBody: {
          participantIds: ids,
          action: 'UNMARK_ATTENDED',
          sendEmailNotification: false,
        },
      }).unwrap()) as { success?: boolean; failedIds?: string[] }
      const failed = response.failedIds?.length ?? 0
      if (failed > 0) {
        setUnmarkError(t('att_unset_error_partial', { count: failed }))
      }
    } catch {
      setUnmarkError(t('att_unset_error'))
    } finally {
      setUnmarkLoading(false)
      setSelectedKeys(new Set())
    }
  }

  /** Generates (first send) or re-sends (subsequent sends) the CoA email for the given ids. */
  async function handleConfirmCoa() {
    if (!confirmCoaIds) return
    setConfirmCoaIds(null)
    setCoaSendError(null)
    try {
      const response = (await sendCertificates({
        eventId: event.id!,
        sendCertificatesBody: {
          participantIds: confirmCoaIds,
          language: normaliseLanguage(i18n.language),
        },
      }).unwrap()) as SendCertificatesResponse
      const failures = response.results?.filter((r) => !r.success) ?? []
      if (failures.length > 0) {
        setCoaSendError(t('coa_send_error_partial', { count: failures.length }))
      }
    } catch {
      setCoaSendError(t('coa_send_error'))
    } finally {
      setSelectedKeys(new Set())
    }
  }

  /**
   * Sends (or re-sends) the "event media available" notification for the given ids. Re-sending
   * overwrites the previous timestamp rather than appending to it, matching EP-15.
   */
  async function handleConfirmMedia() {
    if (!confirmMediaIds) return
    setConfirmMediaIds(null)
    setMediaSendError(null)
    try {
      const response = (await sendMedia({
        eventId: event.id!,
        sendEventMediaNotificationsBody: { participantIds: confirmMediaIds },
      }).unwrap()) as SendEventMediaNotificationResponse
      const failures = response.results?.filter((r) => !r.success) ?? []
      if (failures.length > 0) {
        setMediaSendError(t('media_send_error_partial', { count: failures.length }))
      }
    } catch {
      setMediaSendError(t('media_send_error'))
    } finally {
      setSelectedKeys(new Set())
    }
  }

  async function handleDownloadCoa(row: ParticipantSummary) {
    if (!row.id) return
    const participantName = [row.firstName, row.lastName].filter(Boolean).join(' ') || row.email
    await downloadCertificateOfAttendance({
      eventId: event.id!,
      participantId: row.id,
      participantName,
      language: normaliseLanguage(i18n.language),
    })
  }

  const allSelected = rows.length > 0 && selectedKeys.size === rows.length
  const someSelected = selectedKeys.size > 0 && !allSelected
  const selectedCount = selectedKeys.size

  // Same intersection pattern as every other participant tab's bulk toolbar (see
  // `intersectEligibleIds`): each action only shows once every selected row qualifies. CoA/media
  // sending and unmarking are all gated on the event having ended (a row-independent condition
  // here since every row is already status ATTENDED), but routed through the shared helper for
  // consistency with the per-row-varying actions on the other tabs.
  const selectedRows = rows.filter((r) => selectedKeys.has(r.id!))
  const getRowId = (r: ParticipantSummary) => r.id!
  const bulkCoaIds = intersectEligibleIds(selectedRows, getRowId, () => eventHasEnded)
  const bulkMediaIds = intersectEligibleIds(
    selectedRows,
    getRowId,
    () => eventHasEnded && mediaCount > 0
  )
  const bulkUnmarkIds = intersectEligibleIds(
    selectedRows,
    getRowId,
    (r) => eventHasEnded && !!r.attendedAt
  )
  const bulkDeleteIds = intersectEligibleIds(selectedRows, getRowId, () => true)

  function CoaCell({ row }: { row: ParticipantSummary }) {
    const lastSent = row.certificateLastSentAt
    if (!lastSent) {
      return <span className="text-pax-gray opacity-40">{t('coa_col_never')}</span>
    }
    return (
      <Button
        variant="unstyled"
        onClick={() => void handleDownloadCoa(row)}
        className="text-pax-blue hover:underline"
        title={t('ticket_coa_download')}
      >
        {formatLocalDateTime(lastSent)}
      </Button>
    )
  }

  function MediaCell({ row }: { row: ParticipantSummary }) {
    const lastSent = row.mediaLastSentAt
    if (!lastSent) {
      return <span className="text-pax-gray opacity-40">{t('media_col_never')}</span>
    }
    // Plain text, unlike CoaCell — there's no single per-participant file to open here, just a
    // notification email pointing back to their ticket page.
    return <span className="text-pax-black">{formatLocalDateTime(lastSent)}</span>
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Combined pager + table actions. Bulk actions sit after the pager on the left when
          rows are selected; the attended report stays on the right. */}
      <TablePagination
        total={total}
        offset={offset}
        take={take}
        onOffsetChange={setOffset}
        onTakeChange={setTake}
        toolbarTestId="attended-toolbar"
        selectionActions={
          selectedCount > 0 ? (
            <>
              <span className="text-pax-gray text-xs font-medium">
                {t('n_selected', { count: selectedCount })}
              </span>
              {bulkCoaIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<CertificateIcon />}
                  onClick={() => setConfirmCoaIds(bulkCoaIds)}
                  disabled={coaSending}
                >
                  {t('coa_action_send')}
                </Button>
              )}
              {bulkMediaIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<SendMediaIcon />}
                  onClick={() => setConfirmMediaIds(bulkMediaIds)}
                  disabled={mediaSending}
                >
                  {t('media_action_send')}
                </Button>
              )}
              {bulkUnmarkIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={unmarkLoading}
                  onClick={() => setConfirmUnmarkIds(bulkUnmarkIds)}
                  className="cursor-pointer"
                >
                  {t('att_mark_not_attended')}
                </Button>
              )}
              <Button
                variant="danger"
                size="sm"
                leftIcon={<TrashIcon />}
                onClick={() => setConfirmDelete(bulkDeleteIds)}
              >
                {t('reg_delete')}
              </Button>
            </>
          ) : undefined
        }
        actions={
          <GenerateReportButton
            eventId={event.id!}
            reportType="attended"
            labelKey="report_generate_attended_pdf"
          />
        }
      >
        <div data-testid="mobile-view" className="flex flex-col gap-3 md:hidden">
          {rows.length === 0 ? (
            <p className="text-pax-gray py-6 text-center text-sm">{t('att_empty_attended_tab')}</p>
          ) : (
            rows.map((row) => {
              const isSelected = selectedKeys.has(row.id!)
              const isExpanded = expandedKeys.has(row.id!)
              return (
                <div
                  key={row.id}
                  className="border-pax-purple/20 rounded-2xl border bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5">
                      <input
                        type="checkbox"
                        aria-label={t('table_select_row', { name: row.email })}
                        checked={isSelected}
                        onChange={(e) => handleSelectRow(row.id!, e.target.checked)}
                        className="accent-pax-blue mt-0.5 h-4 w-4 cursor-pointer rounded"
                      />
                      <div>
                        {/* Name, not email — the email address is available below in Details. */}
                        <p className="text-pax-black font-medium">
                          {row.lastName} {row.firstName}
                        </p>
                        <div className="mt-1.5">
                          <RoleSelect
                            role={apiRoleToUiRole(row.role)}
                            registrationId={row.id!}
                            onChange={(id, role) =>
                              requestRoleChange(
                                id,
                                formatFullName(row.firstName, row.lastName),
                                role
                              )
                            }
                          />
                        </div>
                        {row.attendedAt && (
                          <p className="text-pax-gray mt-1 text-xs tabular-nums">
                            {formatLocalDateTime(row.attendedAt, i18n.language)}
                          </p>
                        )}
                        {eventHasEnded && (
                          <>
                            <p className="text-pax-gray mt-1.5 text-xs">
                              {t('coa_col_last_sent')}: <CoaCell row={row} />
                            </p>
                            <p className="text-pax-gray mt-1 text-xs">
                              {t('media_col_last_sent')}: <MediaCell row={row} />
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-0.5">
                      <ConversationButton
                        label={t('conversation_action')}
                        onClick={() => openConversation(row)}
                      />
                      <DeleteButton
                        label={t('reg_delete')}
                        onClick={() => setConfirmDelete([row.id!])}
                      />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-pax-purple/20 mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-xl border bg-slate-50 p-3 text-xs">
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_last_name')}
                        </span>
                        <p className="text-pax-black">{row.lastName || '—'}</p>
                      </div>
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_first_name')}
                        </span>
                        <p className="text-pax-black">{row.firstName}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_email')}
                        </span>
                        <p className="text-pax-black break-all">{row.email}</p>
                      </div>
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_company')}
                        </span>
                        <p className="text-pax-black">{row.companyName ?? '—'}</p>
                      </div>
                      <div>
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_media')}
                        </span>
                        <p className="text-pax-black">
                          {hasGrantedMediaConsent(row) ? t('reg_media_yes') : t('reg_media_no')}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-pax-gray text-[10px] font-semibold uppercase">
                          {t('reg_field_registered_via')}
                        </span>
                        <p className="text-pax-black">
                          {row.registeredByOrganizer ? t('reg_via_organizer') : t('reg_via_self')}
                        </p>
                      </div>
                    </div>
                  )}

                  {isExpanded && (
                    <>
                      <ParticipantTicketButton
                        eventId={event.id}
                        participantId={row.id}
                        fullWidth
                        className="mt-3"
                      />
                      {eventHasEnded && (
                        <SendCertificateButton
                          onClick={() => setConfirmCoaIds([row.id!])}
                          disabled={coaSending}
                          fullWidth
                          className="mt-2"
                        />
                      )}
                      {eventHasEnded && mediaCount > 0 && (
                        <SendMediaButton
                          onClick={() => setConfirmMediaIds([row.id!])}
                          disabled={mediaSending}
                          fullWidth
                          className="mt-2"
                        />
                      )}
                      <MarkNotAttendedButton
                        onClick={() => void handleMarkNotAttended(row.id!)}
                        fullWidth
                        className="mt-2"
                      />
                    </>
                  )}

                  <DetailsToggleButton
                    variant="block"
                    expanded={isExpanded}
                    onClick={() => toggleExpand(row.id!)}
                    className="mt-3"
                  />
                </div>
              )
            })
          )}
        </div>

        {/* Desktop table */}
        <div data-testid="desktop-view" className="hidden md:block">
          <div className="border-pax-purple/30 overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-pax-purple/20 bg-pax-light-purple/60 border-b">
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={t('select_all')}
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected
                        }}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                      />
                    </th>
                    <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                      <SortableColumnHeader
                        label={t('reg_col_name')}
                        field="lastName"
                        activeField={sort}
                        order={order}
                        onSort={toggleSort}
                      />
                    </th>
                    <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                      {t('accept_role_label')}
                    </th>
                    <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                      <SortableColumnHeader
                        label={t('att_col_time')}
                        field="attendedAt"
                        activeField={sort}
                        order={order}
                        onSort={toggleSort}
                      />
                    </th>
                    {eventHasEnded && (
                      <>
                        <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                          {t('coa_col_last_sent')}
                        </th>
                        <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                          {t('media_col_last_sent')}
                        </th>
                      </>
                    )}
                    {/* Header left visually blank (#274); aria-label keeps a real accessible name. */}
                    <th
                      aria-label={t('reg_col_actions')}
                      className="text-pax-gray px-4 py-3 text-right text-xs font-semibold tracking-wider"
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={eventHasEnded ? 7 : 5}
                        className="text-pax-gray px-4 py-10 text-center text-sm"
                      >
                        {t('att_empty_attended_tab')}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const isSelected = selectedKeys.has(row.id!)
                      const isExpanded = expandedKeys.has(row.id!)
                      return (
                        <Fragment key={row.id}>
                          <tr
                            className={[
                              'border-pax-purple/10 border-b transition-colors last:border-0',
                              isSelected ? 'bg-pax-light-blue/40' : 'hover:bg-pax-light-purple/40',
                            ].join(' ')}
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                aria-label={t('table_select_row', { name: row.email })}
                                checked={isSelected}
                                onChange={(e) => handleSelectRow(row.id!, e.target.checked)}
                                className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                              />
                            </td>
                            <td className="text-pax-black px-4 py-3 font-medium">
                              {row.lastName} {row.firstName}
                            </td>
                            <td className="px-4 py-3">
                              <RoleSelect
                                role={apiRoleToUiRole(row.role)}
                                registrationId={row.id!}
                                onChange={(id, role) =>
                                  requestRoleChange(
                                    id,
                                    formatFullName(row.firstName, row.lastName),
                                    role
                                  )
                                }
                              />
                            </td>
                            <td className="text-pax-black px-4 py-3 text-xs tabular-nums">
                              {row.attendedAt ? (
                                formatLocalDateTime(row.attendedAt, i18n.language)
                              ) : (
                                <span className="text-pax-gray opacity-40">
                                  {t('att_col_time_none')}
                                </span>
                              )}
                            </td>
                            {eventHasEnded && (
                              <>
                                <td className="px-4 py-3 text-xs tabular-nums">
                                  <CoaCell row={row} />
                                </td>
                                <td className="px-4 py-3 text-xs tabular-nums">
                                  <MediaCell row={row} />
                                </td>
                              </>
                            )}
                            <td className="px-2 py-2">
                              <div className="flex items-center justify-end gap-1.5">
                                <DetailsToggleButton
                                  expanded={isExpanded}
                                  onClick={() => toggleExpand(row.id!)}
                                />
                                <ConversationButton
                                  label={t('conversation_action')}
                                  onClick={() => openConversation(row)}
                                />
                                <DeleteButton
                                  label={t('reg_delete')}
                                  onClick={() => setConfirmDelete([row.id!])}
                                />
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <ExpandedRow
                              row={row}
                              colSpan={eventHasEnded ? 7 : 5}
                              eventId={event.id}
                              eventHasEnded={eventHasEnded}
                              coaSending={coaSending}
                              mediaEligible={eventHasEnded && mediaCount > 0}
                              mediaSending={mediaSending}
                              onMarkNotAttended={(id) => void handleMarkNotAttended(id)}
                              onSendCoa={(id) => setConfirmCoaIds([id])}
                              onSendMedia={(id) => setConfirmMediaIds([id])}
                            />
                          )}
                        </Fragment>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </TablePagination>

      {coaSendError && (
        <p className="border-pax-red/20 bg-pax-red/5 self-start rounded-lg border px-3 py-1.5 text-xs text-red-600">
          {coaSendError}
        </p>
      )}

      {mediaSendError && (
        <p className="border-pax-red/20 bg-pax-red/5 self-start rounded-lg border px-3 py-1.5 text-xs text-red-600">
          {mediaSendError}
        </p>
      )}

      {unmarkError && (
        <p className="border-pax-red/20 bg-pax-red/5 self-start rounded-lg border px-3 py-1.5 text-xs text-red-600">
          {unmarkError}
        </p>
      )}

      {confirmUnmarkIds && (
        <ConfirmationModal
          title={t('att_unset_confirm_title', { count: confirmUnmarkIds.length })}
          description={t('att_unset_confirm_body')}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmUnmark()}
          onCancel={() => setConfirmUnmarkIds(null)}
        />
      )}

      {confirmCoaIds && (
        <ConfirmationModal
          title={t('coa_confirm_title', { count: confirmCoaIds.length })}
          description={t('coa_confirm_body')}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmCoa()}
          onCancel={() => setConfirmCoaIds(null)}
        />
      )}

      {confirmMediaIds && (
        <ConfirmationModal
          title={t('media_confirm_title', { count: confirmMediaIds.length })}
          description={t('media_confirm_body')}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmMedia()}
          onCancel={() => setConfirmMediaIds(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmationModal
          title={t('reg_delete_confirm', { count: confirmDelete.length })}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Confirm role change (issue #226) — same gate/copy as AcceptedParticipantsTab's */}
      {pendingRoleChange !== null && (
        <PresentationActionModal
          title={t('acc_role_change_confirm_title', {
            name: pendingRoleChange.name,
            role: t(`role_${pendingRoleChange.role.toLowerCase()}`),
          })}
          confirmLabel={t('btn_confirm')}
          showNotify={true}
          showMessage={true}
          messageLabel={t('pres_ask_message_label')}
          messagePlaceholder={t('pres_message_placeholder')}
          onConfirm={(r) => void handleConfirmRoleChange(r)}
          onCancel={() => setPendingRoleChange(null)}
        />
      )}

      {conversationFor && (
        <ParticipantConversationModal
          participantId={conversationFor.id}
          participantName={conversationFor.name}
          onClose={() => setConversationFor(null)}
          actions={
            <Button
              variant="danger"
              size="sm"
              leftIcon={<TrashIcon />}
              onClick={() => {
                const id = conversationFor.id
                setConversationFor(null)
                setConfirmDelete([id])
              }}
            >
              {t('reg_delete')}
            </Button>
          }
        />
      )}
    </div>
  )
}
